import React, { useEffect, useState } from "react";
import {
  Box, Button, Card, CardHeader, CardContent, Dialog, DialogActions, DialogTitle,
  Grid, Avatar, Typography, InputAdornment, IconButton, Tooltip, Switch, TextField, Checkbox
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { capitalize } from "../../utils/helpers";
import { toast } from "react-toastify";
import { EditOutlined, Visibility as VisibilityIcon } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { fDate } from "../../utils/formatTime";
import { apiCategory } from "../../services/AllServices";
import { Loader } from "../../components/CustomLoader";
import { IMAGE_BASE, STATUS_OPTIONS_LIST } from "../../utils/constants";
import Swal from "sweetalert2";
import errorHandler from "../../utils/errorHandler";
import DataTable from "react-data-table-component";
import TableFilter from "../../components/shared/TableFilter";
import CardLayout from "../../layouts/CardLayout";
import BackButton from "../../components/Buttons/BackButton";
import CustomPagination from "../../components/CustomPagination";
import { useBulkSelect } from "../../hooks/useBulkSelect";

export default function SubCategoryTable() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [searchItem, setSearchItem] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  const [warning, setWarning] = useState("");
  const [action, setAction] = useState("");
  const [show, setShow] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusId, setStatusId] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [status, setStatus] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [dataCategory, setDataCategory] = useState({});

  const {
    selectedRows,
    getCheckboxColumn,
    clearSelection,
  } = useBulkSelect(data, null, [debouncedSearch, currentPage, pageSize, refresh, status]);

  useEffect(() => {
    apiCategory.getById({ id })
      .then((response) => setDataCategory(response?.data?.data))
      .catch(errorHandler);
  }, [id]);  

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setDebouncedSearch(searchItem);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchItem]);

  useEffect(() => {
    setIsLoading(true);
    setLoaderDelay(false);

    const body = {
      search: debouncedSearch,
      pageNumber: currentPage,
      pageSize,
      status,
      id
    };

    apiCategory.getSubCategory(body)
      .then((res) => {
        if (res.status === 200) {
          setTimeout(() => {
            setData(res?.data?.data?.subCategories || []);
            setTotalPages(res?.data?.data?.total || 0);
            setLoaderDelay(true);
          }, 500);
        }
      })
      .catch((err) => {
        errorHandler(err);
        setLoaderDelay(false);
      })
      .finally(() => {
        setIsLoading(false);
        setLoaderDelay(false);
      });
  }, [debouncedSearch, currentPage, pageSize, refresh, status]);

  const handlePageChange = (page) => setCurrentPage(page);

  const handleClose = () => setShow(false);

  const deleteData = async (ids) => {
    const isMultiple = Array.isArray(ids);
    Swal.fire({
      text: isMultiple 
        ? `Are you sure you want to delete ${ids.length} selected subcategory(ies)?`
        : "Are you sure you want to delete this Sub Category?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const subCategoryIds = isMultiple ? ids : [ids];
          const payload = subCategoryIds.length === 1 
            ? { id: subCategoryIds[0] }
            : { id: subCategoryIds };
          
          const res = await apiCategory.deleteSubCategory(payload);
          if (res?.status === 200) {
            toast.success(res?.data?.message, { toastId: "success" });
            if (!isMultiple) {
              setData((prevData) =>
                prevData.filter((item) => item._id !== ids)
              );
              setTotalPages((prevTotal) => prevTotal - 1);
            } else {
              setData((prevData) => prevData.filter((item) => !ids.includes(item._id)));
              setTotalPages((prevTotal) => Math.max(0, prevTotal - ids.length));
            }
            clearSelection();
            setRefresh((v) => !v);
          } else {
            toast.error(res?.data?.message, { toastId: "error" });
          }
        } catch (error) {
          errorHandler(error);
        }
      }
    });
  };

  const handleBulkDelete = () => {
    if (selectedRows.length > 0) {
      deleteData(selectedRows);
    }
  };

  const toggleChecked = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      const res = await apiCategory.changeStatus({ id, status: newStatus });
      setCurrentStatus("");

      if (res.status === 200 && res?.data?.message) {
        toast.success(res?.data?.message, { toastId: "success1" });
        setRefresh((v) => !v);
        setShow(false);
      } else {
        toast.error(res?.data?.message, { toastId: "error1" });
        setShow(false);
      }
    } catch (err) {
      setShow(false);
      errorHandler(err);
    }
  };

  const columns = [
    getCheckboxColumn(),
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
    },
     {
         name: "Image",
         cell: (row) => (
           <Avatar
             src={row.categoryImage ? IMAGE_BASE + row.categoryImage[0] : ""}
             alt={row.name}
             sx={{ width: 50, height: 50, border: "2px solid #e2e8f0" }}
           />
         ),
         sortable: false,
       },
       {
         name: " Name",
         sortable: true,
         selector: (row) => capitalize(row.name),
       },
       {
         name: "Description",
         sortable: false,
          selector: (row) => capitalize(row.description),
       },
    {
      name: "Active Listings",
      sortable: true,
      cell: (row) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography fontWeight="bold" color="primary">
            {row.listingCount || 0}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            auctions
          </Typography>
        </Box>
      ),
    },
    {
      name: "Created At",
      sortable: true,
      selector: (row) => fDate(row.createdAt),
    },
    {
      name: "Status",
      cell: (row) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Switch
            checked={row.status === 1}
            onClick={() => {
              setShow(true);
              setCurrentStatus(row.status);
              setStatusId(row._id);
              setAction("status");
              setWarning(
                `Are you sure you want to ${
                  row.status === 1 ? "deactivate" : "activate"
                } this subcategory?`
              );
            }}
            inputProps={{ "aria-label": "controlled" }}
            color="primary"
          />
        
        </Box>
      ),
    },
    {
      name: "Actions",
      center: true,
      cell: (row) => (
        <Box display="flex" gap={1} justifyContent="center">
          <Tooltip title="View Details">
            <IconButton
              color="primary"
              size="small"
              onClick={() => navigate(`/category/viewsubcategory/${row?._id}`)}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              color="primary"
              size="small"
              onClick={() => navigate(`/category/edit-subcategory/${row?._id}`)}
            >
              <EditOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              color="error"
              size="small"
              onClick={() => deleteData(row?._id)}
              disabled={row.activeListings > 0}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <BackButton variant="h6" />
      </Box>
      
      <Card sx={{ background: 'linear-gradient(135deg, #394657, #e96c0f)', color: 'white' }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <Avatar
                src={dataCategory?.categoryImage ? IMAGE_BASE + dataCategory.categoryImage : ""}
                alt={dataCategory?.name}
                sx={{ 
                  width: 120, 
                  height: 120, 
                  border: "4px solid rgba(255,255,255,0.3)",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.3)"
                }}
              />
            </Grid>
            <Grid item xs={12} sm={9} md={10}>
              <Box>
                <Typography variant="h3" fontWeight="bold" gutterBottom>
                  {capitalize(dataCategory?.name || "Parent Category")}
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
                  {capitalize(dataCategory?.description || "No description available")}
                </Typography>
                <Grid container spacing={4} sx={{ mt: 2 }}>
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center">
                      <Typography variant="h4" fontWeight="bold">
                        {data?.length || 0}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Subcategories
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center">
                      <Typography variant="h4" fontWeight="bold">
                        {dataCategory?.listingCount || 0}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Total Listings
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center">
                      <Typography variant="h4" fontWeight="bold">
                        {dataCategory?.status === 1 ? "Active" : "Inactive"}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Status
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center">
                      <Typography variant="h4" fontWeight="bold">
                        {fDate(dataCategory?.createdAt)}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Created Date
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Subcategories Section */}
      <Card>
        {/* <CardLayout> */}
          
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: "flex-end",
              p: 2,
              gap: 2,
            }}
          >
            <TableFilter data={STATUS_OPTIONS_LIST} value={status} setValue={setStatus} filterName="Status" />
            <TextField
              label="Search"
              variant="standard"
              value={searchItem}
              onChange={(e) => setSearchItem(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {searchItem && (
                      <IconButton aria-label="clear-search" onClick={() => setSearchItem("")}>
                        <CloseIcon />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: { xs: "100%", sm: "250px" },
                maxWidth: { xs: "100%", sm: "300px" },
              }}
            />
            {selectedRows.length > 0 && (
              <Button
                variant="contained"
                color="error"
                onClick={handleBulkDelete}
                startIcon={<DeleteIcon />}
                sx={{
                  minWidth: { xs: "100%", sm: "auto" },
                  whiteSpace: "nowrap",
                }}
              >
                Delete Selected ({selectedRows.length})
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(`/category/add-subcategory/${id}`)}
              sx={{
                minWidth: { xs: "100%", sm: "auto" },
                whiteSpace: "nowrap",
              }}
            >
              Add Subcategory
            </Button>
          </Box>
          <CardContent>
            <DataTable
              customStyles={{
                headCells: {
                  style: { fontSize: "15px", fontWeight: "bold" }
                }
              }}
              columns={columns}
              data={data}
              pagination
              paginationServer
              paginationTotalRows={totalPages}
              onChangePage={handlePageChange}
              paginationPerPage={pageSize}
              paginationRowsPerPageOptions={[10, 20, 50]}
              onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
                setPageSize(currentRowsPerPage);
                setCurrentPage(currentPage);
              }}
              progressPending={isLoading || !loaderDelay}
              progressComponent={<Loader />}
              paginationComponent={(props) => (
                <CustomPagination {...props} currentPage={currentPage} />
              )}
              className="custom"
            />
          </CardContent>
        {/* </CardLayout> */}
      </Card>
      <Dialog
          open={show}
          keepMounted
          onClose={handleClose}
          aria-describedby="alert-dialog-slide-description"
          className="popupbox-statis"
        >
          <DialogTitle as="h3">{warning}</DialogTitle>
          <DialogActions className="status-box-action">
            <Button
              variant="outlined"
              onClick={() => {
                if (action === "status") {
                  toggleChecked(statusId, currentStatus);
                }
              }}
            >
              Yes
            </Button>
            <Button variant="outlined" onClick={handleClose}>
              No
            </Button>
          </DialogActions>
        </Dialog>
    </Box>
  );
}
